import { fileConfig } from '@adornis/file-utils/config.js';

/** Server-only: keep the original attachment name for the path-only mail pipeline. */
export async function localAttachmentPath(attachment: {
  _id: string;
  _class: string;
  meta?: { fileName?: string | null; extension?: string | null } | null;
  getFullPath(): string;
}): Promise<string> {
  // These templates are also registered in the client bundle.
  const { createReadStream, createWriteStream } = await import('node:fs');
  const { chmod, lstat, mkdir, mkdtemp, rename, rm, stat } = await import('node:fs/promises');
  const { join } = await import('node:path');
  const { pipeline } = await import('node:stream/promises');
  const { assertInsideFileStore } = await import('@adornis/buchungsheld-db/mails/file-attachment.js');
  const source = await assertInsideFileStore(attachment);

  const sanitize = (value: string) => value.replace(/[/\\\u0000-\u001f\u007f-\u009f]/g, '');
  const id = attachment._id;
  const fileName = sanitize(attachment.meta?.fileName ?? '') || 'file';
  const extension = sanitize(attachment.meta?.extension ?? '') || 'txt';
  const cacheDirectory = join(fileConfig.get('datadir'), 'mail-attachment-cache');
  const directory = join(cacheDirectory, id);
  const destination = join(directory, `${fileName}.${extension}`);
  const sourceStat = await stat(source);
  if (!sourceStat.isFile()) throw new Error('Refusing to attach a non-regular file');

  // Do not follow existing directory symlinks or inherit permissive cache permissions.
  for (const privateDirectory of [cacheDirectory, directory]) {
    try {
      await mkdir(privateDirectory, { mode: 0o700 });
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== 'EEXIST') throw error;
    }
    const existing = await lstat(privateDirectory);
    if (existing.isSymbolicLink() || !existing.isDirectory()) throw new Error('Refusing unsafe mail attachment cache');
    await chmod(privateDirectory, 0o700);
  }

  try {
    const existing = await lstat(destination);
    if (existing.isSymbolicLink() || !existing.isFile()) throw new Error('Refusing unsafe mail attachment cache');
    if (existing.size === sourceStat.size && existing.mtimeMs >= sourceStat.mtimeMs) {
      await chmod(destination, 0o600);
      return destination;
    }
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code !== 'ENOENT') throw error;
  }

  // Publish complete copies atomically, including when several mails prepare the same attachment.
  const stagingDirectory = await mkdtemp(join(directory, '.copy-'));
  try {
    const stagedPath = join(stagingDirectory, 'attachment');
    await pipeline(createReadStream(source), createWriteStream(stagedPath, { flags: 'wx', mode: 0o600 }));
    await rename(stagedPath, destination);
  } finally {
    await rm(stagingDirectory, { recursive: true, force: true });
  }

  return destination;
}
